import { useApiClient } from '@brewardapp/api-client';
import { DATE_FORMAT, Drawer } from '@breward/ui';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

const ScoresheetCommentDrawer = ({ comments, section, opened, onClose, onChange }) => {
  const params = useParams();
  const [t] = useTranslation();
  const { register, handleSubmit, reset, watch } = useForm();

  const api = useApiClient();
  const createScoresheetComment = api.scoresheetComments.useCreateScoresheetComment();
  const markAsRead = api.scoresheets.useMarkScoresheetAsRead?.();

  useEffect(() => {
    if (opened && params?.scoresheetId && markAsRead) {
      markAsRead.mutate({ id: params.scoresheetId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, params?.scoresheetId]);

  const commentValue = watch('comment');

  const onSubmit = (values) => {
    if (!values?.comment?.trim()) return;
    createScoresheetComment.mutate(
      {
        sectionKey: section?.key,
        comment: values?.comment,
        scoresheetId: params?.scoresheetId,
      },
      {
        onSuccess: () => {
          reset();
          onChange?.();
        },
      },
    );
  };

  const sectionComments = comments?.filter(
    (c) => (c?.sectionKey ?? c?.section_key) === section?.key,
  ) ?? [];

  return (
    <Drawer
      title={section?.label}
      opened={opened}
      onClose={onClose}
      footer={
        /* ── Compose bar always pinned at bottom ── */
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex items-end gap-3"
        >
          <textarea
            {...register('comment')}
            placeholder={t('Pages:ScoresheetPage.commentPlaceholder', 'Add a comment...')}
            rows={2}
            className="flex-1 resize-none rounded-2xl border border-white/30 bg-white/40 px-4 py-3 font-sans text-sm text-primary-900 placeholder-content-400 outline-none backdrop-blur transition-colors focus:border-primary-300 focus:bg-white/60 focus:ring-0 dark:border-primary-700/50 dark:bg-content-800/40 dark:text-primary-100 dark:placeholder-content-500 dark:focus:border-primary-600"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(onSubmit)();
              }
            }}
          />
          <button
            type="submit"
            disabled={!commentValue?.trim() || createScoresheetComment.isPending}
            className={classNames(
              'flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl transition-all',
              commentValue?.trim()
                ? 'bg-primary-500 text-white shadow-md hover:bg-primary-600 active:scale-95'
                : 'bg-content-100 text-content-400 dark:bg-content-700 dark:text-content-500 cursor-not-allowed',
            )}
          >
            {createScoresheetComment.isPending ? (
              <div className="icon-[ph--spinner] h-5 w-5 animate-spin" />
            ) : (
              <div className="icon-[ph--paper-plane-tilt] h-5 w-5" />
            )}
          </button>
        </form>
      }
    >
      {/* ── Comment list ── */}
      {sectionComments.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="icon-[ph--chat-circle-dots] h-14 w-14 text-content-300 dark:text-content-600 mb-4" />
          <p className="font-sans text-base font-medium text-content-500 dark:text-content-400">
            {t('Pages:ScoresheetPage.noComments', 'No comments yet')}
          </p>
          <p className="mt-1 font-sans text-sm text-content-400 dark:text-content-500">
            {t('Pages:ScoresheetPage.beFirstToComment', 'Be the first to add a comment below.')}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {sectionComments.map((comment, idx) => (
            <div
              key={comment?.id || idx}
              className="rounded-2xl border border-white/30 bg-white/40 px-4 py-3 shadow-sm backdrop-blur dark:border-primary-800/40 dark:bg-content-800/30"
            >
              <p className="font-sans text-sm text-primary-900 dark:text-primary-100 leading-relaxed">
                {comment?.comment}
              </p>
              <div className="mt-2 flex items-center justify-between">
                <span className="font-sans text-xs text-content-400 dark:text-content-500">
                  {dayjs(comment?.insertedAt)?.format(DATE_FORMAT)}
                </span>
                <span className="font-sans text-xs italic text-content-500 dark:text-content-400">
                  {comment?.user?.data?.firstName} {comment?.user?.data?.lastName}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Drawer>
  );
};

export default ScoresheetCommentDrawer;
